import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import { Button, Dropdown, Tag, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilterCircleXmark } from "@fortawesome/free-solid-svg-icons/faFilterCircleXmark";
import { faTruck } from "@fortawesome/free-solid-svg-icons/faTruck";
import { ArrowsAltOutlined, CaretDownOutlined, CheckOutlined, DownloadOutlined, FormOutlined, LinkOutlined, LoadingOutlined, ReloadOutlined, ShrinkOutlined, } from "@ant-design/icons";
import PaginatedFilterableTable, { PaginatedFilterableTableExpandKey } from "../../../components/table/PaginatedFilterableTable";
import { ConsignmentActionEnum, ConsignmentStatusEnum } from "../api/Representation";
import ConsignmentDeliveryDate from "../../../components/ConsignmentDeliveryDate";
import { getDivisionName, isUserMultiTenant, shouldShowDivision } from "../../../../../swzutils/UserPermissionsUtil";
import AddressSuburbPopover from "../AddressSuburbPopover";
import ConsignmentFreightPopover from "../ConsignmentFreightPopover";
import { CurrencyFormat, DateTimeFormat } from "../../../components/FormattersTS";
import PermissionButton from "../../../components/PermissionButton";
import PaymentApprovalStage from "../../../enums/PaymentApprovalStage";
import ConsignmentFreightBillingTable from "./ConsignmentFreightBillingTable";
import ValOrDash from "../../../components/ValOrDash";
import ConsignmentResource from "../api/ConsignmentResource";
import { axiosErrorHandler } from "../../../../../swzutils";
import ConsignmentFreightItemModal from "../ConsignmentFreightItemModal";
import ConsignmentPatchCustomerRefModal from "../ConsignmentCustomerRefModal";
import DebtorNotePopover from "../../debtors/DebtorNotePopover";
import UserSummary from "../../../components/UserSummary";
import qtyRangeUOMSearchTableFilter from "../../../components/table/QtyRangeUOMSearchTableFilter";
import { useAppSelector } from "../../../../../store";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../../store/slice/auth";
const ConsignmentBillingApprovalsTable = () => {
    const [approvingConsignmentIds, setApprovingConsignmentIds] = useState([]);
    const [editingConsignment, setEditingConsignment] = useState(null);
    const [editingFreightLine, setEditingFreightLine] = useState(null);
    const [showEditCustomerRefModal, setShowEditCustomerRefModal] = useState(false);
    const [selectedConsignmentIds, setSelectedConsignmentIds] = useState([]);
    const tableRef = useRef(null);
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const divisionName = getDivisionName(loggedInUser, selectedTenant);
    const refresh = () => {
        tableRef.current?.fetch();
    };
    const resetToInitialFilters = () => {
        tableRef.current?.resetToInitialFilters();
    };
    const downloadCsv = (summaryOnly) => {
        const suffix = summaryOnly ? "summary" : "details";
        const additionalQueryParams = {
            "summaryOnly": summaryOnly
        };
        tableRef.current?.downloadCurrentFilterAsCsv("deliveries_billable_" + suffix, "/csv", additionalQueryParams);
    };
    const approveDebtorInvoice = (consignment) => {
        if (approvingConsignmentIds.includes(consignment.id)) {
            return;
        }
        addRemoveApprovingConsignmentId(consignment.id, true);
        ConsignmentResource.approveDebtorInvoice(consignment.id, response => tableRef.current?.updateRecord(response), axiosErrorHandler, () => addRemoveApprovingConsignmentId(consignment.id, false));
    };
    const addRemoveApprovingConsignmentId = (consignmentId, add) => {
        setApprovingConsignmentIds(ids => {
            const idsSet = new Set(ids);
            add ? idsSet.add(consignmentId) : idsSet.delete(consignmentId);
            return [...idsSet];
        });
    };
    const onEditNetFreightClicked = (consignment, charge) => {
        setEditingConsignment(consignment);
        setEditingFreightLine(charge.freight);
    };
    const hideEditConsignmentFreightModal = () => {
        setEditingConsignment(null);
        setEditingFreightLine(null);
    };
    const onEditCustomerRefClicked = (consignment) => {
        setEditingConsignment(consignment);
        setShowEditCustomerRefModal(true);
    };
    const hideEditCustomerRefModal = () => {
        setShowEditCustomerRefModal(false);
        setEditingConsignment(null);
        setEditingFreightLine(null);
    };
    const refreshConsignment = (consignment) => {
        addRemoveApprovingConsignmentId(consignment.id, true);
        ConsignmentResource.getBillable(consignment.id, updated => tableRef.current?.updateRecord(updated), axiosErrorHandler, () => addRemoveApprovingConsignmentId(consignment.id, false));
    };
    const onBulkApproveClicked = () => {
        if (approvingConsignmentIds.length > 0 || selectedConsignmentIds.length <= 0) {
            return;
        }
        setApprovingConsignmentIds(selectedConsignmentIds);
        ConsignmentResource.approveBulkDebtorInvoices(selectedConsignmentIds, () => {
            setSelectedConsignmentIds([]);
            refresh();
        }, axiosErrorHandler, () => setApprovingConsignmentIds([]));
    };
    const onTableRowSelectionChange = (rowKeys) => {
        setSelectedConsignmentIds(rowKeys);
    };
    const columns = [{
            title: 'Delivery Date',
            key: 'deliveryDateKey',
            className: "nowrap",
            render: (value, record) => {
                return (_jsx(ConsignmentDeliveryDate, { consignment: record }));
            },
            filter: {
                type: 'dateRange',
                requestKey: 'deliveryDateOnOrAfter',
                requestKeyDateTo: 'deliveryDateOnOrBefore'
            },
            sorter: true,
            sortFilter: {
                requestKey: 'orderBy',
                ascending: 'DELIVERY_FROM_ASC',
                descending: 'DELIVERY_FROM_DESC'
            }
        }, {
            title: 'Completed At',
            key: 'completedDate',
            className: "nowrap",
            render: (value, record) => (_jsx(DateTimeFormat, { value: record.completedAt })),
            filter: {
                type: 'dateRange',
                requestKey: 'completedOnOrAfterDate',
                requestKeyDateTo: 'completedOnOrBeforeDate'
            },
            sorter: true,
            sortFilter: {
                requestKey: 'orderBy',
                ascending: 'COMPLETED_AT_ASC',
                descending: 'COMPLETED_AT_DESC'
            }
        }, {
            title: 'Con. No.',
            dataIndex: 'consignmentRef',
            className: "nowrap",
            filter: {
                type: 'text',
                placeholder: 'Search ref',
                requestKey: 'consignmentRefLike'
            },
            render: (value, consignment) => (_jsx("u", { children: _jsxs("a", { href: `#/app/consignments/view/${consignment.id}`, children: [consignment.consignmentRef, " ", _jsx(LinkOutlined, {})] }) }))
        }, {
            title: 'Ord. Ref.',
            key: 'salesOrderRef',
            dataIndex: 'salesOrderRef',
            className: "nowrap",
            filter: {
                type: 'text',
                placeholder: 'Search ref',
                requestKey: 'salesOrderRefLike'
            },
            render: (value, consignment) => {
                return consignment.salesOrder ?
                    _jsx("u", { children: _jsxs("a", { href: `#/app/sales-orders/view/${consignment.salesOrder.id}`, children: [consignment.salesOrder.ref, " ", _jsx(LinkOutlined, {})] }) })
                    :
                        _jsx(ValOrDash, { val: consignment.salesOrderRef });
            }
        }, {
            title: 'Sales Person',
            key: 'salesPersonName',
            render: (text, consignment) => {
                return _jsx(UserSummary, { user: consignment.salesAdminUser });
            },
            filter: {
                type: 'text',
                placeholder: 'Sales Person',
                requestKey: 'salesPersonNameLike',
                presenceFilter: {
                    placeholder: 'Has sales person?',
                    labelPresent: 'Sales person is present',
                    labelAbsent: 'Sales person is absent',
                    requestKey: 'hasSalesPerson'
                }
            }
        }, {
            title: 'Created By',
            render: (text, consignment) => {
                return _jsx(UserSummary, { user: consignment.createdBy });
            }
        }, {
            title: 'Cust. Ref.',
            key: 'customerRef',
            dataIndex: 'customerRef',
            className: "nowrap",
            sorter: true,
            sortFilter: {
                requestKey: 'orderBy',
                ascending: 'CUSTOMER_REF_ASC',
                descending: 'CUSTOMER_REF_DESC',
            },
            filter: {
                type: 'text',
                placeholder: 'Search ref',
                requestKey: 'customerRefLike'
            },
            render: (value, consignment) => {
                if (consignment.debtorInvoice) {
                    return consignment.customerRef;
                }
                return (_jsxs("a", { className: "edit", onClick: () => onEditCustomerRefClicked(consignment), children: [consignment.customerRef, " \u00A0 ", _jsx(FormOutlined, { style: { fontSize: '1.3em' } })] }));
            }
        }, {
            title: 'Profile',
            dataIndex: 'tenantName',
            sorter: false,
            condition: !selectedTenant && isUserMultiTenant(loggedInUser)
        }, {
            title: divisionName,
            dataIndex: 'division',
            sorter: false,
            condition: shouldShowDivision(loggedInUser, selectedTenant),
            filter: {
                type: 'text',
                placeholder: divisionName,
                requestKey: 'divisionNameLike'
            },
            render: (value, consignment) => {
                if (consignment.division) {
                    return _jsx(Tag, { children: consignment.division.name }, consignment.division.id);
                }
                return null;
            }
        }, {
            title: _jsx(Tooltip, { placement: "top", title: "Marketplace", trigger: "hover", children: _jsx(FontAwesomeIcon, { icon: faTruck }) }),
            key: 'marketplaceListed',
            filterMultiple: false,
            filter: {
                type: 'enum',
                requestKey: 'marketplaceListed',
                enums: [
                    { text: 'Yes', value: 'true' },
                    { text: 'No', value: 'false' },
                ],
                filterMultiple: false
            },
            render: (text, consignment) => {
                return consignment.marketplaceListings && consignment.marketplaceListings.length ? "Yes" : "No";
            }
        }, {
            title: 'Customer',
            key: "customerNameLike",
            dataIndex: ['customer', 'name'],
            filter: {
                type: "text",
                placeholder: 'Search name',
                requestKey: 'customerNameLike'
            },
            sorter: true,
            sortFilter: {
                requestKey: 'orderBy',
                ascending: 'CUSTOMER_NAME_ASC',
                descending: 'CUSTOMER_NAME_DESC',
            },
            render: (value, consignment) => {
                return (_jsxs(DebtorNotePopover, { customerId: consignment.customer?.id, children: ["\u00A0", value] }));
            }
        }, {
            title: 'Customer Group',
            key: "customerGroupName",
            dataIndex: ['customerGroup', 'name'],
            filter: {
                type: "text",
                placeholder: 'Search group',
                requestKey: 'customerGroupNameLike'
            }
        }, {
            title: 'Delivery Location',
            key: 'addressStatus',
            render: (value, consignment) => (_jsx(AddressSuburbPopover, { address: consignment.deliveryAddress, title: consignment.deliveryCompanyName, isPickup: false }))
        }, {
            title: 'Vehicle Type',
            key: 'vehicleType',
            filter: {
                type: "text",
                placeholder: 'Search vehicle type',
                requestKey: 'vehicleTypeDescriptionLike'
            },
            render: (text, consignment) => {
                if (consignment.assignedVehicles?.length) {
                    const cav = consignment.assignedVehicles.find(cav => cav.action == ConsignmentActionEnum.DELIVER);
                    if (cav) {
                        return cav.vehicle.type.description;
                    }
                }
                return "";
            }
        }, {
            title: 'Freight',
            filter: {
                type: "custom",
                requestKey: 'freight',
                builder: qtyRangeUOMSearchTableFilter('freightQtyEqualOrGreaterThan', 'freightQtyEqualOrLessThan', 'freightUnitOfMeasure', {
                    placeholder: 'Product',
                    requestKey: 'freightProductLike'
                })
            },
            render: (value, consignment) => (_jsx(ConsignmentFreightPopover, { consignment: consignment, forcePopover: false }))
        }, {
            title: 'Amount (ex GST)',
            dataIndex: 'totalBillableAmountExTax',
            render: (value /*, consignment*/) => (_jsx(CurrencyFormat, { value: value }))
        }, {
            title: 'Invoice Ref',
            dataIndex: ['debtorInvoice', 'publicId'],
        }, {
            title: 'Billing',
            dataIndex: 'debtorInvoiceApprovedAt',
            filterMultiple: false,
            filter: {
                type: 'enum',
                requestKey: 'debtorInvoiceApproved',
                enums: [
                    { text: 'Approved', value: 'true' },
                    { text: 'Pending', value: 'false' },
                ],
                filterMultiple: false
            },
            render: (value, consignment) => {
                if (consignment.debtorInvoiceApprovedAt) {
                    return PaymentApprovalStage.APPROVED.renderTag();
                }
                return PaymentApprovalStage.PENDING.renderTag();
            }
        }, {
            title: 'Action',
            key: 'action',
            render: (value, consignment) => {
                if (consignment.debtorInvoiceApprovedAt || consignment.debtorInvoice) {
                    return "";
                }
                return (_jsx(PermissionButton, { permissions: ['admin:consignmentBillable:approveDebtorInvoice'], onClick: () => approveDebtorInvoice(consignment), children: approvingConsignmentIds.includes(consignment.id) ? _jsx(LoadingOutlined, {}) : _jsx(CheckOutlined, {}) }));
            }
        }];
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "table-operations clearfix", children: ["\u00A0", _jsxs(Button, { htmlType: "button", onClick: onBulkApproveClicked, disabled: approvingConsignmentIds.length > 0 || selectedConsignmentIds.length <= 0, title: "Approve Selected", children: [_jsx(CheckOutlined, {}), "\u00A0Bulk Approve\u00A0", selectedConsignmentIds.length, " Deliveries"] }), _jsx(Button, { className: "pull-right", onClick: refresh, title: "Refresh", icon: _jsx(ReloadOutlined, {}) }), _jsx(Button, { className: "pull-right", onClick: resetToInitialFilters, title: "Reset Filters", icon: _jsx(FontAwesomeIcon, { icon: faFilterCircleXmark }) }), _jsx(PermissionButton, { permissions: ['admin:consignmentBillableExport:csv'], className: "pull-right", onClick: () => downloadCsv(true), title: "Download Summary CSV", icon: _jsx(DownloadOutlined, {}), children: "Summary CSV" }), _jsx(PermissionButton, { permissions: ['admin:consignmentBillableExport:csv'], className: "pull-right", onClick: () => downloadCsv(false), title: "Download Detailed CSV", icon: _jsx(DownloadOutlined, {}), children: "Detailed CSV" }), _jsx(Dropdown, { destroyOnHidden: true, menu: {
                            items: [{
                                    key: "expandAll",
                                    icon: _jsx(ArrowsAltOutlined, {}),
                                    label: "Expand All",
                                    onClick: () => tableRef.current?.expandOrCollapse(PaginatedFilterableTableExpandKey.EXPAND_ALL)
                                }, {
                                    key: "collapseAll",
                                    icon: _jsx(ShrinkOutlined, {}),
                                    label: "Collapse All",
                                    onClick: () => tableRef.current?.expandOrCollapse(PaginatedFilterableTableExpandKey.COLLAPSE_ALL)
                                }]
                        }, children: _jsxs(Button, { children: [_jsx(CaretDownOutlined, {}), " Collapse/Expand"] }) })] }), _jsx(PaginatedFilterableTable, { tableKey: 'consignmentBillableApprovalsTable', ref: tableRef, dataUri: "/admin/v1/consignments/billable", columns: columns, minWidthBeforeScrolling: 1000, refreshOnSelectedTenantChanged: true, initialFilters: {
                    "status": [ConsignmentStatusEnum.COMPLETED],
                    "debtorInvoiceApproved": [false]
                }, initialExpandKey: PaginatedFilterableTableExpandKey.COLLAPSE_ALL, expandable: {
                    expandedRowRender: (consignment /*, index, indent */) => (_jsx(ConsignmentFreightBillingTable, { consignment: consignment, onEditNetFreight: charge => onEditNetFreightClicked(consignment, charge) })),
                    expandRowByClick: false,
                    rowExpandable: (consignment) => consignment.charges?.length > 0
                }, rowSelection: {
                    type: 'checkbox',
                    preserveSelectedRowKeys: true,
                    selectedRowKeys: selectedConsignmentIds,
                    onChange: onTableRowSelectionChange,
                    getCheckboxProps: (record) => ({
                        disabled: !!record.debtorInvoiceApprovedAt
                    }),
                } }), _jsx(ConsignmentFreightItemModal, { open: !!editingFreightLine && !!editingConsignment, consignment: editingConsignment, editingFreightLine: editingFreightLine, editMode: 'EDIT_NET', canChangeSavedProduct: false, showTrackingCode: false, showWasteRecordId: false, showDescription: true, onCancel: hideEditConsignmentFreightModal, onUpdate: ( /*freightItem*/) => {
                    refreshConsignment(editingConsignment);
                    hideEditConsignmentFreightModal();
                } }), _jsx(ConsignmentPatchCustomerRefModal, { open: showEditCustomerRefModal, consignment: editingConsignment, onCancel: hideEditCustomerRefModal, onUpdated: () => {
                    refreshConsignment(editingConsignment);
                    hideEditCustomerRefModal();
                } })] }));
};
export default ConsignmentBillingApprovalsTable;
